/**
 * Lodestone utility functions
 */

import { METERS_PER_MILE, METERS_PER_KM, FEET_PER_METER } from './constants';

/**
 * Calculate the distance between two coordinates using the Haversine formula
 */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Format distance in meters to human-readable string
 */
export function formatDistance(meters: number, useMetric = false): string {
    if (!meters || meters <= 0) return '';
    if (useMetric) {
        const km = meters / METERS_PER_KM;
        return km >= 1 ? `${km.toFixed(1)} km` : `${Math.round(meters)} m`;
    }
    const miles = meters / METERS_PER_MILE;
    return `${miles.toFixed(1)} mi`;
}

/**
 * Format elevation in meters to human-readable string
 */
export function formatElevation(meters: number | null, useMetric = false): string {
    if (meters == null) return '';
    if (useMetric) {
        return `${Math.round(meters).toLocaleString()} m`;
    }
    const feet = meters * FEET_PER_METER;
    return `${Math.round(feet).toLocaleString()} ft`;
}

/**
 * Sample coordinates array to reduce points while preserving shape
 */
export function sampleCoordinates<T>(coords: T[], maxPoints = 100): T[] {
    if (!coords || coords.length <= maxPoints) return coords;
    const step = Math.ceil(coords.length / maxPoints);
    const sampled: T[] = [];
    for (let i = 0; i < coords.length; i += step) {
        sampled.push(coords[i]);
    }
    // Always include last point
    if (sampled[sampled.length - 1] !== coords[coords.length - 1]) {
        sampled.push(coords[coords.length - 1]);
    }
    return sampled;
}

/**
 * Elevation profile used as fallback when coordinates lack elevation data
 */
export interface ElevationProfile {
    distances: number[];
    elevations: number[];
}

/** Detect @mention query at cursor position. Returns {start, query} or null. */
export function detectMention(input: HTMLInputElement): { start: number; query: string } | null {
    const c = input.selectionStart || 0;
    const m = input.value.substring(0, c).match(/(^|\s)@(\S*)$/);
    return m ? { start: c - m[2].length - 1, query: m[2] } : null;
}

/** Insert @Name at mention start position, place cursor after, sync data-bind. */
export function insertMention(name: string, mentionStart: number): void {
    const inp = document.getElementById('comment-input') as HTMLInputElement;
    if (!inp) return;
    const before = inp.value.substring(0, mentionStart);
    const after = inp.value.substring(inp.selectionStart || inp.value.length);
    const mention = '@' + name + ' ';
    inp.value = before + mention + after;
    const pos = before.length + mention.length;
    inp.setSelectionRange(pos, pos);
    inp.focus();
    inp.dispatchEvent(new Event('input', { bubbles: true }));
}

